// Mock API - simulates backend calls with async delays
import { loadData, saveData } from './data';

const delay = (ms = 400) => new Promise(resolve => setTimeout(resolve, ms));

let nextId = 100;
const genId = (prefix) => `${prefix}${nextId++}`;

// Auth
export async function login(username, password) {
  await delay(600);
  const data = loadData();
  const user = data.users.find(u => u.username === username && u.password === password);
  if (!user) throw new Error('Invalid username or password');
  const { password: _, ...safeUser } = user;
  return { token: 'mock-jwt-' + user.id, user: safeUser };
}

export async function requestPasswordReset(email) {
  await delay(500);
  return { message: 'If an account with that email exists, a reset link has been sent.' };
}

// IdP Configs
export async function getIdpConfigs() {
  await delay();
  return loadData().idpConfigs;
}

export async function createIdpConfig(config) {
  await delay(500);
  const data = loadData();
  const newConfig = { id: genId('idp'), createdAt: new Date().toISOString().split('T')[0], status: 'active', ...config };
  data.idpConfigs.push(newConfig);
  data.auditLogs.unshift({
    id: genId('al'),
    userId: getCurrentUserId(),
    userName: getCurrentUserName(),
    action: `Created IdP config "${config.name}"`,
    timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
    category: 'config',
  });
  saveData(data);
  return newConfig;
}

export async function deleteIdpConfig(id) {
  await delay(300);
  const data = loadData();
  const config = data.idpConfigs.find(c => c.id === id);
  data.idpConfigs = data.idpConfigs.filter(c => c.id !== id);
  if (config) {
    data.auditLogs.unshift({
      id: genId('al'),
      userId: getCurrentUserId(),
      userName: getCurrentUserName(),
      action: `Deleted IdP config "${config.name}"`,
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
      category: 'config',
    });
  }
  saveData(data);
}

// Policies
export async function getPolicies() {
  await delay();
  return loadData().policies;
}

export async function createPolicy(policy) {
  await delay(500);
  const data = loadData();
  const newPolicy = { id: genId('pol'), createdAt: new Date().toISOString().split('T')[0], status: 'active', ...policy };
  data.policies.push(newPolicy);
  data.auditLogs.unshift({
    id: genId('al'),
    userId: getCurrentUserId(),
    userName: getCurrentUserName(),
    action: `Created policy "${policy.name}"`,
    timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
    category: 'policy',
  });
  saveData(data);
  return newPolicy;
}

export async function deletePolicy(id) {
  await delay(300);
  const data = loadData();
  const policy = data.policies.find(p => p.id === id);
  data.policies = data.policies.filter(p => p.id !== id);
  if (policy) {
    data.auditLogs.unshift({
      id: genId('al'),
      userId: getCurrentUserId(),
      userName: getCurrentUserName(),
      action: `Deleted policy "${policy.name}"`,
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
      category: 'policy',
    });
  }
  saveData(data);
}

// Migration Status
export async function getMigrationStatus() {
  await delay();
  return loadData().migrationStatus;
}

// Audit Logs
export async function getAuditLogs() {
  await delay();
  return loadData().auditLogs;
}

// Profile
export async function getProfile() {
  await delay(300);
  const data = loadData();
  const userId = getCurrentUserId();
  const user = data.users.find(u => u.id === userId);
  if (!user) throw new Error('User not found');
  const { password: _, ...safeUser } = user;
  return safeUser;
}

export async function updateProfile(updates) {
  await delay(500);
  const data = loadData();
  const userId = getCurrentUserId();
  const idx = data.users.findIndex(u => u.id === userId);
  if (idx === -1) throw new Error('User not found');
  data.users[idx] = { ...data.users[idx], ...updates };
  data.auditLogs.unshift({
    id: genId('al'),
    userId,
    userName: `${data.users[idx].firstName} ${data.users[idx].lastName}`,
    action: 'Updated profile information',
    timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
    category: 'profile',
  });
  saveData(data);
  const { password: _, ...safeUser } = data.users[idx];
  return safeUser;
}

export async function changePassword(currentPassword, newPassword) {
  await delay(500);
  const data = loadData();
  const userId = getCurrentUserId();
  const user = data.users.find(u => u.id === userId);
  if (!user) throw new Error('User not found');
  if (user.password !== currentPassword) throw new Error('Current password is incorrect');
  user.password = newPassword;
  data.auditLogs.unshift({
    id: genId('al'),
    userId,
    userName: `${user.firstName} ${user.lastName}`,
    action: 'Changed password',
    timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
    category: 'profile',
  });
  saveData(data);
  return { message: 'Password changed successfully' };
}

// Team
export async function getTeam() {
  await delay();
  return loadData().team;
}

export async function inviteTeamMember(member) {
  await delay(500);
  const data = loadData();
  const newMember = {
    id: genId('t'),
    userId: null,
    name: member.name,
    email: member.email,
    role: member.role,
    status: 'pending',
    joinedAt: new Date().toISOString().split('T')[0],
  };
  data.team.push(newMember);
  data.auditLogs.unshift({
    id: genId('al'),
    userId: getCurrentUserId(),
    userName: getCurrentUserName(),
    action: `Invited ${member.name} (${member.email}) as ${member.role}`,
    timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
    category: 'team',
  });
  saveData(data);
  return newMember;
}

export async function updateTeamMemberRole(memberId, newRole) {
  await delay(400);
  const data = loadData();
  const member = data.team.find(m => m.id === memberId);
  if (!member) throw new Error('Team member not found');
  const oldRole = member.role;
  member.role = newRole;
  data.auditLogs.unshift({
    id: genId('al'),
    userId: getCurrentUserId(),
    userName: getCurrentUserName(),
    action: `Changed ${member.name}'s role from ${oldRole} to ${newRole}`,
    timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
    category: 'team',
  });
  saveData(data);
  return member;
}

export async function removeTeamMember(memberId) {
  await delay(400);
  const data = loadData();
  const member = data.team.find(m => m.id === memberId);
  data.team = data.team.filter(m => m.id !== memberId);
  if (member) {
    data.auditLogs.unshift({
      id: genId('al'),
      userId: getCurrentUserId(),
      userName: getCurrentUserName(),
      action: `Removed ${member.name} from the team`,
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
      category: 'team',
    });
  }
  saveData(data);
}

// Helpers to get current user from localStorage token
function getCurrentUserId() {
  const auth = localStorage.getItem('kamig_auth');
  if (auth) {
    const { user } = JSON.parse(auth);
    return user?.id || 'u1';
  }
  return 'u1';
}

function getCurrentUserName() {
  const auth = localStorage.getItem('kamig_auth');
  if (auth) {
    const { user } = JSON.parse(auth);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown';
  }
  return 'Unknown';
}
